// src/components/CryptoDashboard.js
import React, { useState, useEffect } from 'react';
import staticDataService from '../api/staticDataService';
import CryptoChart from './CryptoChart';

const CryptoDashboard = () => {
  // Zustand für den ausgewählten Zeitraum
  const [timeRange, setTimeRange] = useState('24h');
  
  // Zustand für die Kryptowährungsdaten
  const [cryptoData, setCryptoData] = useState({});
  
  // Zustand für die aktuellen Preise
  const [currentPrices, setCurrentPrices] = useState({});
  
  // Zustand für die letzte Aktualisierung
  const [lastUpdate, setLastUpdate] = useState(new Date(0));
  
  // Zustand für Lade- und Fehlerstatus
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Zustände für Sonifizierung
  const [sonicEnabled, setSonicEnabled] = useState(true);
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(true);
  
  // Regelmäßiges Abrufen der Daten aus dem statischen Dienst
  useEffect(() => {
    // Funktion zum Abrufen der Daten vom statischen Dienst
    const fetchFromStaticService = () => {
      try {
        // Aktuelle Preise
        const { data: prices, lastUpdate: pricesUpdate } = staticDataService.getCurrentPrices();
        setCurrentPrices(prices);
        
        // Historische Daten je nach Zeitraum
        const { data: historical, lastUpdate: historicalUpdate } = staticDataService.getHistoricalData(timeRange);
        setCryptoData(historical);
        
        // Neueres der beiden Updates als Zeitpunkt anzeigen
        setLastUpdate(new Date(Math.max(pricesUpdate, historicalUpdate)));
        
        // Lade-Status aktualisieren
        if (Object.keys(prices).length > 0 && Object.keys(historical).length > 0) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Fehler beim Abrufen der Daten vom statischen Dienst:", err);
        setError("Daten konnten nicht geladen werden. Bitte später erneut versuchen.");
      }
    };
    
    // Anfängliches Abrufen
    fetchFromStaticService();
    
    // Setze Intervall für UI-Updates (öfter als die eigentlichen API-Abrufe)
    const intervalId = setInterval(fetchFromStaticService, 5000); // Alle 5 Sekunden UI aktualisieren
    
    return () => clearInterval(intervalId);
  }, [timeRange]); // Nur bei Änderung des Zeitraums neu abonnieren
  
  // Toggle-Funktionen für Sonifizierung
  const toggleSonic = () => {
    setSonicEnabled(!sonicEnabled);
    // Hier später Code für Aktivierung/Deaktivierung der Kurs-Sonifizierung
  };
  
  const toggleHeartbeat = () => {
    setHeartbeatEnabled(!heartbeatEnabled);
    // Hier später Code für Aktivierung/Deaktivierung des Herzschlagtons
  };
  
  // Render-Funktion für das Dashboard
  return (
    <div className="crypto-dashboard p-4 bg-gray-900 text-gray-100 min-h-screen">
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-gray-100">Crypto Market</h2>
            <button
              onClick={toggleSonic}
              className={`px-3 py-1 text-sm font-medium rounded ${
                sonicEnabled 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-700 text-gray-300'
              } hover:opacity-90 transition-colors`}
            >
              Sonic
            </button>
            <button
              onClick={toggleHeartbeat}
              className={`p-2 rounded-full ${
                heartbeatEnabled 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-700 text-gray-300'
              } hover:opacity-90 transition-colors`}
              title="Heartbeat Sound"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32c-5.15-4.67-8.55-7.75-8.55-11.53 0-3.08 2.42-5.5 5.5-5.5 1.74 0 3.41.81 4.5 2.09 1.09-1.28 2.76-2.09 4.5-2.09 3.08 0 5.5 2.42 5.5 5.5 0 3.78-3.4 6.86-8.55 11.54l-1.45 1.31z" />
              </svg>
            </button>
          </div>
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => setTimeRange('1h')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                timeRange === '1h' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              } border border-gray-600`}
            >
              1h
            </button>
            <button
              onClick={() => setTimeRange('24h')}
              className={`px-4 py-2 text-sm font-medium ${
                timeRange === '24h' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              } border-t border-b border-gray-600`}
            >
              24h
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                timeRange === '30d' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              } border border-gray-600`}
            >
              30d
            </button>
          </div>
        </div>
        <div className="flex justify-between items-center text-sm text-gray-400">
          <div className="flex items-center space-x-4">
            <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
            <span>provided by LearnTradeEvolve.com</span>
            <a 
              href="https://app.zignaly.com/profit-sharing/65528b460c8937a7470ad408" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <span className="mr-1">Visit us on Zignaly</span>
              <img src="/Z1.png" alt="Zignaly" className="h-4" />
            </a>
          </div>
        </div>
      </div>
      
      {/* Fehler- und Ladeanimation */}
      {error && (
        <div className="p-4 mb-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-md text-red-300">
          {error}
        </div>
      )}
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(cryptoData).map(([coin, data]) => {
            // Aktuelle Preisinformationen
            const price = currentPrices[coin]?.usd || 0;
            const priceChange = currentPrices[coin]?.usd_24h_change || 0;
            
            return (
              <CryptoChart 
                key={coin}
                coin={coin}
                symbol={coin.toUpperCase()}
                data={data}
                timeRange={timeRange}
                currentPrice={price}
                priceChange={priceChange}
              />
            );
          })}
        </div>
      )}
      
      {/* Footer mit rechtlichen Links - aktualisiert */}
      <footer className="mt-8 pt-4 border-t border-gray-700 text-gray-400">
        <div className="text-center mb-2">
          <h3 className="text-sm font-medium mb-2">Legal Information</h3>
          <div className="flex flex-wrap justify-center space-x-4">
            <a href="https://learntradeevolve.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">
              Privacy Policy
            </a>
            <a href="https://learntradeevolve.com/legal-notice/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">
              Legal Notice
            </a>
            <a href="https://learntradeevolve.com/disclaimer/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">
              Disclaimer
            </a>
          </div>
        </div>
        <div className="text-center text-sm pb-4">
          LearnTradeEvolve – Automated Trading<br />
          Copyright © LearnTradeEvolve co. Ancorise GmbH. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default CryptoDashboard;
